'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { FvdErrorScreen, ErrorPrimary } from '@/components/errors/FvdErrorScreen';

/**
 * Where the one way on goes. The dashboard and the hub have their own
 * boundaries inside their shells; this one still catches a failure in their
 * layouts, so it sends people back to that area's front page, with a whole
 * page load. Anywhere else (an event page, a ticket) the place is worth
 * keeping, so the one action is to try the same page again.
 */
function wayOnFor(pathname: string | null): { href: string; label: string } | null {
  if (pathname?.startsWith('/dashboard')) return { href: '/dashboard', label: 'Back to your dashboard' };
  if (pathname?.startsWith('/hub')) return { href: '/hub/home', label: 'Back to myfitvent' };
  return null;
}

export default function Error({
  error,
  reset,
  retry,
}: {
  error: Error & { digest?: string };
  reset: () => void;
  /** Next 16.3: re-fetches and re-renders the segment. `reset` only re-renders, which cannot recover a server error. */
  retry?: () => void;
}) {
  const pathname = usePathname();
  useEffect(() => {
    // The digest is the server's reference for this error; no message or
    // stack reaches the logs from here.
    console.error('Route error', error.digest ?? '');
  }, [error]);

  const way = wayOnFor(pathname);
  return (
    <FvdErrorScreen
      lockup={pathname?.startsWith('/hub') ? 'myfitvent' : 'fitvent'}
      title="Something went wrong"
      body={way ? "This page didn’t load. Go back and try again from there." : "This page didn’t load. It may be a passing problem, so try again in a moment."}
      primary={
        way ? (
          <ErrorPrimary href={way.href} fullLoad>{way.label}</ErrorPrimary>
        ) : (
          <ErrorPrimary onClick={retry ?? reset}>Try again</ErrorPrimary>
        )
      }
      footnote={error.digest ? `Reference ${error.digest}` : undefined}
    />
  );
}
